// CallAgents - Gestion des agents d'appels vocaux (entrants/sortants)
import { useState, useEffect } from "react";
import {
  Phone,
  Plus,
  BarChart3,
  Clock,
  PhoneIncoming,
  PhoneOutgoing,
  CheckCircle,
  XCircle,
  Loader2,
  Mic,
  Bot,
  Edit,
  Archive,
  Power,
  PowerOff,
  Save,
  Volume2,
  Brain,
  ArchiveRestore,
  ChevronDown,
  ChevronUp,
  Rocket,
  Lock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import IntentEditor from "../management/IntentEditor";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper pour obtenir les headers d'authentification
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function CallAgents({ onNavigateToAudrey }) {
  const { user } = useAuth();
  
  // Tab state - inbound ou outbound
  const [activeTab, setActiveTab] = useState("inbound");
  
  // Agents state
  const [inboundAgents, setInboundAgents] = useState([]);
  const [outboundAgents, setOutboundAgents] = useState([]);
  const [archivedAgents, setArchivedAgents] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  
  // Selected agent state
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [voices, setVoices] = useState([]);
  
  // UI states
  const [showIntentEditor, setShowIntentEditor] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  
  // Phone status state
  const [phoneStatus, setPhoneStatus] = useState(null);
  const [loadingPhoneStatus, setLoadingPhoneStatus] = useState(true);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    company_name: "",
    persona: "",
    greeting_message: "Bonjour, comment puis-je vous aider ?",
    voice_id: "",
    voice_name: "",
    twilio_number: "",
    sms_number: "",
    sms_enabled: false,
    agent_type: "inbound"
  });

  const isAdmin = user?.rôle === "admin";
  const canEdit = user?.rôle === "admin" || user?.rôle === "editor";

  // Fetch phone status on load
  const fetchPhoneStatus = async () => {
    setLoadingPhoneStatus(true);
    try {
      const response = await axios.get(`${API}/api/twilio/phone-status`, {
        headers: getAuthHeaders()
      });
      setPhoneStatus(response.data);
    } catch (error) {
      console.error("Error fetching phone status:", error);
      setPhoneStatus({
        status: "error",
        last_error: "Impossible de vérifier le statut du numéro",
        checks: []
      });
    } finally {
      setLoadingPhoneStatus(false);
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchVoices();
    fetchArchivedAgents();
    fetchPhoneStatus();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      fetchAgentDetails(selectedAgent.id);
    }
  }, [selectedAgent]);

  // Reset selection when changing tabs
  useEffect(() => {
    setSelectedAgent(null);
    setAgentDetails(null);
  }, [activeTab]);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/api/agents`, {
        headers: getAuthHeaders()
      });
      const allAgents = response.data.agents || [];
      
      // Séparer les agents par type
      const inbound = allAgents.filter(a => a.agent_type !== "outbound");
      const outbound = allAgents.filter(a => a.agent_type === "outbound");
      
      setInboundAgents(inbound);
      setOutboundAgents(outbound);
      
      // Sélectionner le premier agent entrant par défaut
      if (inbound.length > 0 && !selectedAgent) {
        setSelectedAgent(inbound[0]);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
      // Fallback sur les anciennes données Audrey
      fetchLegacyAudreyData();
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedAgents = async () => {
    try {
      const response = await axios.get(`${API}/api/agents/archived/list`, {
        headers: getAuthHeaders()
      });
      setArchivedAgents(response.data.agents || []);
    } catch (error) {
      console.error("Error fetching archived agents:", error);
    }
  };

  const handleArchiveAgent = async () => {
    if (!selectedAgent || selectedAgent.id === "audrey_legacy") return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/api/agents/${selectedAgent.id}/archive`, {}, {
        headers: getAuthHeaders()
      });
      
      toast.success("Agent archivé avec succès");
      setShowArchiveConfirm(false);
      setSelectedAgent(null);
      fetchAgents();
      fetchArchivedAgents();
    } catch (error) {
      console.error("Error archiving agent:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'archivage");
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreAgent = async (agentId) => {
    try {
      await axios.post(`${API}/api/agents/${agentId}/restore`, {}, {
        headers: getAuthHeaders()
      });
      
      toast.success("Agent restauré avec succès");
      fetchAgents();
      fetchArchivedAgents();
    } catch (error) {
      console.error("Error restoring agent:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la restauration");
    }
  };

  const fetchLegacyAudreyData = async () => {
    try {
      const [statsRes, callsRes] = await Promise.all([
        axios.get(`${API}/api/calls/stats/overview`).catch(() => ({ data: {} })),
        axios.get(`${API}/api/calls?limit=5`).catch(() => ({ data: { calls: [] } }))
      ]);
      
      const audreyAgent = {
        id: "audrey_legacy",
        name: "Audrey",
        description: "Agent vocal IA francophone pour la gestion des appels entrants",
        company_name: "MPA",
        status: "active",
        agent_type: "inbound",
        voice_name: "Sarah",
        phone_number: null
      };
      
      setInboundAgents([audreyAgent]);
      setSelectedAgent(audreyAgent);
      setStats(statsRes.data);
      setRecentCalls(callsRes.data.calls || []);
    } catch (error) {
      console.error("Error fetching legacy data:", error);
    }
  };

  const fetchAgentDetails = async (agentId) => {
    if (agentId === "audrey_legacy") {
      try {
        const [statsRes, callsRes] = await Promise.all([
          axios.get(`${API}/api/calls/stats/overview`).catch(() => ({ data: {} })),
          axios.get(`${API}/api/calls?limit=5`).catch(() => ({ data: { calls: [] } }))
        ]);
        setStats(statsRes.data);
        setRecentCalls(callsRes.data.calls || []);
        setAgentDetails({
          id: "audrey_legacy",
          name: "Audrey",
          description: "Agent vocal IA francophone",
          status: "active",
          agent_type: "inbound",
          voice: { voice_name: "Sarah", provider: "elevenlabs" },
          twilio: { phone_number: "+33939035444", sms_enabled: true }
        });
      } catch (error) {
        console.error("Error fetching legacy details:", error);
      }
      return;
    }
    
    setLoadingDetails(true);
    try {
      const [detailsRes, statusRes, callsRes] = await Promise.all([
        axios.get(`${API}/api/agents/${agentId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/agents/${agentId}/status`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/calls?agent_id=${agentId}&limit=5`, { headers: getAuthHeaders() })
      ]);
      
      setAgentDetails(detailsRes.data);
      setStats({
        today: statusRes.data.calls_today,
        week: statusRes.data.calls_week,
        total: statusRes.data.calls_total
      });
      setRecentCalls(callsRes.data.calls || []);
    } catch (error) {
      console.error("Error fetching agent details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchVoices = async () => {
    try {
      const response = await axios.get(`${API}/api/agents/voices/available`, {
        headers: getAuthHeaders()
      });
      setVoices(response.data.voices || []);
    } catch (error) {
      setVoices([
        { id: "EXAVITQu4vr4xnSDxMaL", name: "Sarah", gender: "female" },
        { id: "21m00Tcm4TlvDq8ikWAM", name: "Rachel", gender: "female" },
        { id: "pNInz6obpgDQGcFmaJgB", name: "Adam", gender: "male" }
      ]);
    }
  };

  const handleCreateAgent = async () => {
    if (!formData.name.trim()) {
      toast.error("Le nom est requis");
      return;
    }
    
    const currentAgents = activeTab === "inbound" ? inboundAgents : outboundAgents;
    const existingAgent = currentAgents.find(
      a => a.name.toLowerCase().trim() === formData.name.toLowerCase().trim()
    );
    if (existingAgent) {
      toast.error(`Un agent avec le nom "${formData.name}" existe déjà`);
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        agent_type: activeTab // inbound ou outbound
      };
      
      const response = await axios.post(`${API}/api/agents`, payload, {
        headers: getAuthHeaders()
      });
      
      toast.success("Agent créé avec succès!");
      setShowCreateModal(false);
      resetForm();
      fetchAgents();
      
      if (response.data.agent) {
        setSelectedAgent(response.data.agent);
      }
    } catch (error) {
      console.error("Error creating agent:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAgent = async () => {
    if (!selectedAgent || selectedAgent.id === "audrey_legacy") {
      toast.error("Impossible de modifier cet agent");
      return;
    }
    
    const currentAgents = activeTab === "inbound" ? inboundAgents : outboundAgents;
    const existingAgent = currentAgents.find(
      a => a.id !== selectedAgent.id && 
           a.name.toLowerCase().trim() === formData.name.toLowerCase().trim()
    );
    if (existingAgent) {
      toast.error(`Un agent avec le nom "${formData.name}" existe déjà`);
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/api/agents/${selectedAgent.id}`, {
        name: formData.name,
        description: formData.description,
        company_name: formData.company_name,
        persona: formData.persona,
        greeting_message: formData.greeting_message
      }, { headers: getAuthHeaders() });
      
      toast.success("Agent mis à jour!");
      setShowEditModal(false);
      fetchAgents();
      fetchAgentDetails(selectedAgent.id);
    } catch (error) {
      console.error("Error updating agent:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAgent = async () => {
    if (!selectedAgent || selectedAgent.id === "audrey_legacy") {
      toast.error("Impossible d'archiver cet agent");
      return;
    }
    
    setSaving(true);
    try {
      await axios.delete(`${API}/api/agents/${selectedAgent.id}`, {
        headers: getAuthHeaders()
      });
      
      toast.success("Agent archivé");
      setShowDeleteConfirm(false);
      setSelectedAgent(null);
      fetchAgents();
    } catch (error) {
      console.error("Error archiving agent:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'archivage");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (agentId, currentStatus) => {
    if (agentId === "audrey_legacy") return;
    
    const endpoint = currentStatus === "active" ? "deactivate" : "activate";
    try {
      await axios.post(`${API}/api/agents/${agentId}/${endpoint}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(currentStatus === "active" ? "Agent désactivé" : "Agent activé");
      fetchAgents();
    } catch (error) {
      toast.error("Erreur lors du changement de statut");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      company_name: "",
      persona: "",
      greeting_message: "Bonjour, comment puis-je vous aider ?",
      voice_id: "",
      voice_name: "",
      twilio_number: "",
      sms_number: "",
      sms_enabled: false,
      agent_type: activeTab
    });
  };

  const openEditModal = () => {
    if (agentDetails) {
      setFormData({
        name: agentDetails.name || "",
        description: agentDetails.description || "",
        company_name: agentDetails.company_name || "",
        persona: agentDetails.persona || "",
        greeting_message: agentDetails.greeting_message || "",
        voice_id: agentDetails.voice?.voice_id || "",
        voice_name: agentDetails.voice?.voice_name || "",
        twilio_number: agentDetails.twilio?.phone_number || "",
        sms_number: agentDetails.twilio?.sms_number || "",
        sms_enabled: agentDetails.twilio?.sms_enabled || false,
        agent_type: agentDetails.agent_type || "inbound"
      });
    }
    setShowEditModal(true);
  };

  // Si on affiche l'éditeur d'intents
  if (showIntentEditor && selectedAgent) {
    return (
      <IntentEditor 
        agentId={selectedAgent.id}
        agentName={selectedAgent.name}
        onClose={() => setShowIntentEditor(false)}
      />
    );
  }

  const currentAgents = activeTab === "inbound" ? inboundAgents : outboundAgents;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            Agents Vocaux
          </h1>
          <p className="text-zinc-400 mt-1">Gérez vos agents vocaux IA pour les appels entrants et sortants</p>
        </div>
      </div>

      {/* Onglets Entrant / Sortant */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-800/50 border border-white/10 p-1">
          <TabsTrigger 
            value="inbound" 
            className="flex items-center gap-2 data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
            data-testid="inbound-tab"
          >
            <PhoneIncoming className="w-4 h-4" />
            Appels Entrants
            <Badge variant="secondary" className="ml-1 bg-emerald-500/20 text-emerald-400 text-xs">
              {inboundAgents.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="outbound" 
            className="flex items-center gap-2 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400"
            data-testid="outbound-tab"
          >
            <PhoneOutgoing className="w-4 h-4" />
            Appels Sortants
            <Badge variant="secondary" className="ml-1 bg-zinc-700 text-zinc-400 text-xs">
              Coming Soon
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* Contenu Appels Entrants */}
        <TabsContent value="inbound" className="mt-6">
          {/* Statut du numéro Twilio - Uniquement pour les entrants */}
          <div className={`p-4 rounded-xl border mb-6 ${
            loadingPhoneStatus 
              ? 'bg-zinc-800/50 border-zinc-700' 
              : phoneStatus?.status === 'operational' 
                ? 'bg-emerald-500/10 border-emerald-500/30' 
                : phoneStatus?.status === 'warning'
                  ? 'bg-amber-500/10 border-amber-500/30'
                  : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {loadingPhoneStatus ? (
                  <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
                ) : phoneStatus?.status === 'operational' ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                ) : phoneStatus?.status === 'warning' ? (
                  <Clock className="w-5 h-5 text-amber-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${
                      loadingPhoneStatus 
                        ? 'text-zinc-400' 
                        : phoneStatus?.status === 'operational' 
                          ? 'text-emerald-400' 
                          : phoneStatus?.status === 'warning'
                            ? 'text-amber-400'
                            : 'text-red-400'
                    }`}>
                      {loadingPhoneStatus 
                        ? 'Vérification du numéro...' 
                        : phoneStatus?.status === 'operational' 
                          ? 'Numéro opérationnel' 
                          : phoneStatus?.status === 'warning'
                            ? 'Numéro en avertissement'
                            : 'Numéro en erreur'}
                    </span>
                    {phoneStatus?.phone_number && (
                      <Badge variant="outline" className="text-xs">
                        {phoneStatus.phone_number}
                      </Badge>
                    )}
                  </div>
                  {phoneStatus?.last_error && (
                    <p className="text-xs text-red-400 mt-1">
                      {phoneStatus.last_error}
                    </p>
                  )}
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchPhoneStatus}
                disabled={loadingPhoneStatus}
                className="text-zinc-400 hover:text-white"
              >
                {loadingPhoneStatus ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Actualiser'
                )}
              </Button>
            </div>
          </div>

          {/* Grille agents entrants */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Liste des agents */}
            <div className="lg:col-span-1">
              <Card className="bg-zinc-900/50 border-white/10">
                <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Bot className="w-5 h-5 text-emerald-400" />
                    Agents Entrants ({inboundAgents.length})
                  </CardTitle>
                  {canEdit && (
                    <Button 
                      size="sm"
                      onClick={() => { resetForm(); setShowCreateModal(true); }}
                      className="bg-emerald-500 hover:bg-emerald-600"
                      data-testid="create-inbound-agent-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Créer
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {loading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
                    </div>
                  ) : inboundAgents.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">
                      <PhoneIncoming className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucun agent entrant</p>
                      <p className="text-xs mt-1">Créez votre premier agent pour les appels entrants</p>
                    </div>
                  ) : (
                    inboundAgents.map(agent => (
                      <AgentCard 
                        key={agent.id}
                        agent={agent}
                        isSelected={selectedAgent?.id === agent.id}
                        onClick={() => setSelectedAgent(agent)}
                        agentType="inbound"
                      />
                    ))
                  )}

                  {/* Section Agents Archivés */}
                  {archivedAgents.filter(a => a.agent_type !== "outbound").length > 0 && (
                    <ArchivedSection 
                      agents={archivedAgents.filter(a => a.agent_type !== "outbound")}
                      showArchived={showArchived}
                      setShowArchived={setShowArchived}
                      onRestore={handleRestoreAgent}
                    />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Détails de l'agent */}
            <div className="lg:col-span-2">
              {selectedAgent ? (
                <AgentDetailsPanel 
                  agent={selectedAgent}
                  agentDetails={agentDetails}
                  stats={stats}
                  canEdit={canEdit}
                  isAdmin={isAdmin}
                  onEdit={openEditModal}
                  onArchive={() => setShowArchiveConfirm(true)}
                  onToggleStatus={handleToggleStatus}
                  onIntentEditor={() => setShowIntentEditor(true)}
                  onNavigateToAudrey={onNavigateToAudrey}
                />
              ) : (
                <Card className="bg-zinc-900/50 border-white/10 h-96 flex items-center justify-center">
                  <div className="text-center">
                    <PhoneIncoming className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-500">Sélectionnez un agent pour voir ses détails</p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Contenu Appels Sortants - Coming Soon */}
        <TabsContent value="outbound" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="py-16">
              <div className="text-center max-w-md mx-auto">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-6">
                  <Rocket className="w-10 h-10 text-blue-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  Appels Sortants
                </h2>
                <p className="text-zinc-400 mb-6">
                  {"La fonctionnalité d'appels sortants automatisés arrive bientôt ! Vos agents IA pourront initier des appels pour des rappels, des confirmations de rendez-vous, des enquêtes de satisfaction et bien plus encore."}
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
                  <Lock className="w-4 h-4" />
                  <span>Fonctionnalité en cours de développement</span>
                </div>
                
                {/* Aperçu des fonctionnalités à venir */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                  <div className="p-4 rounded-xl bg-zinc-800/50 border border-white/5">
                    <PhoneOutgoing className="w-5 h-5 text-blue-400 mb-2" />
                    <h3 className="text-sm font-medium text-white">Rappels automatiques</h3>
                    <p className="text-xs text-zinc-500 mt-1">Confirmation de RDV, relances</p>
                  </div>
                  <div className="p-4 rounded-xl bg-zinc-800/50 border border-white/5">
                    <BarChart3 className="w-5 h-5 text-purple-400 mb-2" />
                    <h3 className="text-sm font-medium text-white">Enquêtes de satisfaction</h3>
                    <p className="text-xs text-zinc-500 mt-1">Collecte de feedback client</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Création */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeTab === "inbound" ? (
                <PhoneIncoming className="w-5 h-5 text-emerald-400" />
              ) : (
                <PhoneOutgoing className="w-5 h-5 text-blue-400" />
              )}
              Créer un agent {activeTab === "inbound" ? "entrant" : "sortant"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Configurez les paramètres de base de votre nouvel agent
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="general" className="mt-4">
            <TabsList className="bg-zinc-800">
              <TabsTrigger value="general">Général</TabsTrigger>
              <TabsTrigger value="voice">Voix</TabsTrigger>
              <TabsTrigger value="phone">Téléphone</TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4 mt-4">
              <div>
                <Label>{"Nom de l'agent *"}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ex: Audrey Exemple"
                  className="bg-zinc-800 border-white/10 mt-1"
                  data-testid="agent-name-input"
                />
              </div>
              <div>
                <Label>Entreprise</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  placeholder="Ex: SOCIÉTÉ EXEMPLE Solutions"
                  className="bg-zinc-800 border-white/10 mt-1"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Agent vocal pour la gestion des appels..."
                  className="bg-zinc-800 border-white/10 mt-1"
                />
              </div>
              <div>
                <Label>Personnalité (persona)</Label>
                <Textarea
                  value={formData.persona}
                  onChange={(e) => setFormData({...formData, persona: e.target.value})}
                  placeholder="Décrivez la personnalité de l'agent..."
                  className="bg-zinc-800 border-white/10 mt-1"
                  rows={3}
                />
              </div>
              <div>
                <Label>{"Message d'accueil"}</Label>
                <Textarea
                  value={formData.greeting_message}
                  onChange={(e) => setFormData({...formData, greeting_message: e.target.value})}
                  className="bg-zinc-800 border-white/10 mt-1"
                />
              </div>
            </TabsContent>
            
            <TabsContent value="voice" className="space-y-4 mt-4">
              <div>
                <Label>Voix ElevenLabs</Label>
                <Select 
                  value={formData.voice_id} 
                  onValueChange={(value) => {
                    const voice = voices.find(v => v.id === value);
                    setFormData({...formData, voice_id: value, voice_name: voice?.name || ""});
                  }}
                >
                  <SelectTrigger className="bg-zinc-800 border-white/10 mt-1">
                    <SelectValue placeholder="Sélectionnez une voix" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-white/10">
                    {voices.map(voice => (
                      <SelectItem key={voice.id} value={voice.id} className="text-white">
                        {voice.name} ({voice.gender})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            
            <TabsContent value="phone" className="space-y-4 mt-4">
              <div>
                <Label>Numéro Twilio (entrant)</Label>
                <Input
                  value={formData.twilio_number}
                  onChange={(e) => setFormData({...formData, twilio_number: e.target.value})}
                  placeholder="+33..."
                  className="bg-zinc-800 border-white/10 mt-1"
                />
              </div>
              <div>
                <Label>Numéro SMS (si différent)</Label>
                <Input
                  value={formData.sms_number}
                  onChange={(e) => setFormData({...formData, sms_number: e.target.value})}
                  placeholder="+33..."
                  className="bg-zinc-800 border-white/10 mt-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.sms_enabled}
                  onCheckedChange={(checked) => setFormData({...formData, sms_enabled: checked})}
                />
                <Label>{"Activer l'envoi de SMS"}</Label>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-white/10">
              Annuler
            </Button>
            <Button 
              onClick={handleCreateAgent} 
              disabled={saving} 
              className={activeTab === "inbound" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-blue-500 hover:bg-blue-600"}
              data-testid="create-agent-submit-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {"Créer l'agent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Édition */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{"Modifier l'agent"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>{"Nom de l'agent"}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-zinc-800 border-white/10 mt-1"
              />
            </div>
            <div>
              <Label>Entreprise</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                className="bg-zinc-800 border-white/10 mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-zinc-800 border-white/10 mt-1"
              />
            </div>
            <div>
              <Label>Personnalité</Label>
              <Textarea
                value={formData.persona}
                onChange={(e) => setFormData({...formData, persona: e.target.value})}
                className="bg-zinc-800 border-white/10 mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label>{"Message d'accueil"}</Label>
              <Textarea
                value={formData.greeting_message}
                onChange={(e) => setFormData({...formData, greeting_message: e.target.value})}
                className="bg-zinc-800 border-white/10 mt-1"
              />
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            {isAdmin && selectedAgent?.id !== "audrey_legacy" && (
              <Button 
                variant="destructive" 
                onClick={() => { setShowEditModal(false); setShowDeleteConfirm(true); }}
                className="mr-auto"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archiver
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-white/10">
              Annuler
            </Button>
            <Button onClick={handleUpdateAgent} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmation Archivage */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{"Archiver l'agent ?"}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {`L'agent "${selectedAgent?.name}" sera archivé et retiré de la liste active.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="border-white/10">
              Annuler
            </Button>
            <Button onClick={handleDeleteAgent} disabled={saving} className="bg-zinc-700 hover:bg-zinc-600">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
              Archiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmation Archivage (bouton header) */}
      <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-orange-400" />
              {"Archiver l'agent ?"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {`L'agent "${selectedAgent?.name}" sera archivé et ne sera plus visible dans la liste principale. Vous pourrez le restaurer à tout moment depuis la section "Agents archivés".`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveConfirm(false)} className="border-white/10">
              Annuler
            </Button>
            <Button 
              onClick={handleArchiveAgent} 
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
              Archiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Composant pour afficher une carte d'agent
function AgentCard({ agent, isSelected, onClick, agentType }) {
  return (
    <div
      onClick={onClick}
      className={`p-4 rounded-xl border cursor-pointer transition-all ${
        isSelected
          ? agentType === "inbound" 
            ? 'bg-emerald-500/20 border-emerald-500/50'
            : 'bg-blue-500/20 border-blue-500/50'
          : 'bg-zinc-800/50 border-white/10 hover:bg-zinc-800'
      }`}
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            agentType === "inbound"
              ? 'bg-gradient-to-br from-emerald-400 to-teal-500'
              : 'bg-gradient-to-br from-blue-400 to-purple-500'
          }`}>
            <Mic className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-white">{agent.name}</p>
            <p className="text-xs text-zinc-500">{agent.company_name || "Agent vocal"}</p>
          </div>
        </div>
        <Badge 
          variant="secondary" 
          className={agent.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}
        >
          {agent.status === 'active' ? 'Actif' : 'Inactif'}
        </Badge>
      </div>
      <p className="text-sm text-zinc-400 line-clamp-2">{agent.description}</p>
      {agent.phone_number && (
        <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1">
          <Phone className="w-3 h-3" />
          {agent.phone_number}
        </p>
      )}
    </div>
  );
}

// Composant pour la section des agents archivés
function ArchivedSection({ agents, showArchived, setShowArchived, onRestore }) {
  return (
    <div className="mt-4 pt-4 border-t border-white/10">
      <button
        onClick={() => setShowArchived(!showArchived)}
        className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 w-full"
      >
        <Archive className="w-4 h-4" />
        <span>Agents archivés ({agents.length})</span>
        {showArchived ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
      </button>
      
      {showArchived && (
        <div className="mt-3 space-y-2">
          {agents.map(agent => (
            <div
              key={agent.id}
              className="p-3 rounded-lg bg-zinc-800/30 border border-white/5 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center">
                  <Mic className="w-4 h-4 text-zinc-500" />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">{agent.name}</p>
                  <p className="text-xs text-zinc-600">{agent.company_name || "Agent archivé"}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRestore(agent.id)}
                className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
              >
                <ArchiveRestore className="w-4 h-4 mr-1" />
                Restaurer
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Composant pour le panneau de détails de l'agent
function AgentDetailsPanel({ 
  agent, 
  agentDetails, 
  stats, 
  canEdit, 
  isAdmin, 
  onEdit, 
  onArchive, 
  onToggleStatus, 
  onIntentEditor,
  onNavigateToAudrey 
}) {
  const isInbound = agent.agent_type !== "outbound";
  
  return (
    <Card className="bg-zinc-900/50 border-white/10">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
              isInbound
                ? 'bg-gradient-to-br from-emerald-400 to-teal-500'
                : 'bg-gradient-to-br from-blue-400 to-purple-500'
            }`}>
              <Mic className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{agent.name}</h2>
              <p className="text-zinc-400">{agent.description}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge variant="secondary" className={agent.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}>
                  {agent.status === 'active' ? <><CheckCircle className="w-3 h-3 mr-1" />Actif 24/7</> : <><XCircle className="w-3 h-3 mr-1" />Inactif</>}
                </Badge>
                <Badge variant="secondary" className={isInbound ? "bg-emerald-500/20 text-emerald-400" : "bg-blue-500/20 text-blue-400"}>
                  {isInbound ? <><PhoneIncoming className="w-3 h-3 mr-1" />Entrant</> : <><PhoneOutgoing className="w-3 h-3 mr-1" />Sortant</>}
                </Badge>
                <Badge variant="secondary" className="bg-blue-500/20 text-blue-400">
                  <Volume2 className="w-3 h-3 mr-1" />
                  {agentDetails?.voice?.voice_name || agent.voice_name || "Sarah"}
                </Badge>
                <Badge variant="secondary" className="bg-purple-500/20 text-purple-400">
                  GPT-4o
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canEdit && agent.id !== "audrey_legacy" && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onIntentEditor}
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                >
                  <Brain className="w-4 h-4 mr-1" />
                  Intents
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onToggleStatus(agent.id, agent.status)}
                  className="border-white/10"
                >
                  {agent.status === 'active' ? (
                    <><PowerOff className="w-4 h-4 mr-1" />Désactiver</>
                  ) : (
                    <><Power className="w-4 h-4 mr-1" />Activer</>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onEdit}
                  className="border-white/10"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onArchive}
                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                >
                  <Archive className="w-4 h-4 mr-1" />
                  Archiver
                </Button>
              </>
            )}
            {/* Bouton Dashboard complet pour Audrey */}
            {(agent.id === "audrey_legacy" || agent.name?.toLowerCase().includes("audrey")) && (
              <Button 
                onClick={() => onNavigateToAudrey?.()}
                className="bg-purple-500 hover:bg-purple-600"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Cockpit Audrey
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-6">
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-white/5">
              <p className="text-xs text-zinc-500 uppercase">{"Aujourd'hui"}</p>
              <p className="text-2xl font-bold text-white mt-1">{stats.today || 0}</p>
              <p className="text-xs text-zinc-500">appels</p>
            </div>
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-white/5">
              <p className="text-xs text-zinc-500 uppercase">Cette semaine</p>
              <p className="text-2xl font-bold text-white mt-1">{stats.week || 0}</p>
              <p className="text-xs text-zinc-500">appels</p>
            </div>
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-white/5">
              <p className="text-xs text-zinc-500 uppercase">Total</p>
              <p className="text-2xl font-bold text-white mt-1">{stats.total || 0}</p>
              <p className="text-xs text-zinc-500">appels</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
